import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, secureFetch } from './common';
import ContractViewer from './ContractViewer';
import DiscountPercentageField from './DiscountPercentageField';
import { buildUpsellRollout } from './upsellRollout';
import QuotesList from './QuotesList';

const GTM_API_URL = process.env.REACT_APP_GTM_API_URL || 'https://newton-gtm.vercel.app';
const EXPRESS_API_URL = process.env.REACT_APP_DENTAL_API_URL || 'https://dentalexpressserver.azurewebsites.net';

// Per-product discount options, in dollars off per location per month
const PRICE_DISCOUNT_OPTIONS = [50, 100, 150];

// ----- SDB enterprise auto-detect -----
// When the rep selects this company in the autocomplete, the quote auto-flips
// into the SDB invoice-billing track: SDB-only product dropdown, onboarding
// waived, billingMode=invoice_net30 with AP invoice email, dedicated Dropbox
// Sign template downstream. Match is case-insensitive on the trimmed name.
const SDB_COMPANY_NAMES = new Set(['specialty dental brands', 'vardiman black holdings llc']);
const SDB_INVOICE_EMAIL = 'apinvoice@sdbmail.com';
const isSdbCompanyName = (name) => SDB_COMPANY_NAMES.has((name || '').trim().toLowerCase());

// Stripe product ID → location product key(s). A value may be a single key or
// an array of keys when one product enables multiple capabilities.
const PRODUCT_ID_MAP = {
  // Current Newton subscriptions
  // 'prod_Umzr9uPMyFWvDr': ['phones', 'inbound'], // Newton Phones (new — VoIP + inbound; disabled)
  'prod_UmzpaLD43SmZrc': 'outbound', // Newton Engagement
  'prod_UmzsF1Y8jXRh3R': 'insuranceVerification', // Newton Insurance
  'prod_Umzv3QE6kidiCE': 'marketAttribution', // Newton Marketing
  'prod_UlrQKd74AUSvZT': 'pay', // Newton Pay
  // Legacy product IDs (kept so already-sent quotes still provision correctly)
  'prod_U9fXTLaV1ydpPm': 'phones',
  'prod_U3JZZLelza95Bi': 'inbound',
  'prod_U3JaHrbDIaHsnS': 'outbound',
  'prod_U9jZyfMGYqys4K': 'insuranceVerification',
  'prod_USpBopkSCpgViX': 'marketAttribution',
  'prod_UVT2Rgs4MRdJnT': 'digitalForms',
  'prod_UaJjBYW2oqRZu5': 'inbound',
};

// Newton Insurance products — selecting one of these prices reveals the Zuub
// cost calculator, which lets the rep set a custom per-location price.
const INSURANCE_PRODUCT_IDS = new Set(
  Object.entries(PRODUCT_ID_MAP).filter(([, key]) => key === 'insuranceVerification').map(([id]) => id)
);
const isInsurancePrice = (price) => !!price && INSURANCE_PRODUCT_IDS.has(price.product_id);

// What Zuub charges us per eligibility verification, in dollars. Internal —
// never shown to the customer. Override at build time if the contract changes.
const ZUUB_COST_PER_VERIFICATION = parseFloat(process.env.REACT_APP_ZUUB_COST_PER_VERIFICATION) || 0.31;

// Debounced Attio autocomplete input
const AttioAutocomplete = ({ value, onChange, placeholder, endpoint, inputStyle: extraStyle }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  const fetchSuggestions = (query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${GTM_API_URL}/api/v2/attio?type=${endpoint}&q=${encodeURIComponent(query)}&limit=8`);
        const data = await res.json();
        if (data.success && data.results) {
          setSuggestions(data.results);
          setShowDropdown(data.results.length > 0);
        }
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    fetchSuggestions(val);
  };

  const handleSelect = (name) => {
    onChange(name);
    setShowDropdown(false);
    setSuggestions([]);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
        placeholder={placeholder}
        style={extraStyle}
        autoComplete="off"
      />
      {showDropdown && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: '#fff', border: '1px solid #d1d5db', borderTop: 'none',
          borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: 200, overflowY: 'auto',
        }}>
          {loading && <div style={{ padding: '8px 12px', fontSize: 13, color: '#9ca3af' }}>Searching...</div>}
          {suggestions.map(s => (
            <div
              key={s.id}
              onClick={() => handleSelect(s.name)}
              style={{
                padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                borderBottom: '1px solid #f3f4f6',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              {s.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const QuoteBuilder = () => {
  const [activeSubTab, setActiveSubTab] = useState('builder');

  // Form state
  const [orgName, setOrgName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [stripePriceIds, setStripePriceIds] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [billingDeferralDays, setBillingDeferralDays] = useState(0);
  const [existingStripeSubscriptionId, setExistingStripeSubscriptionId] = useState('');
  const [multiLocationDiscount, setMultiLocationDiscount] = useState(0); // % off subscriptions
  const [bundleDiscount, setBundleDiscount] = useState(0); // % off subscriptions
  const [priceDiscounts, setPriceDiscounts] = useState({}); // priceId → $/location/month off
  const [insuranceApptsPerMonth, setInsuranceApptsPerMonth] = useState(''); // per location; feeds the Zuub cost calculator
  const [insuranceCustom, setInsuranceCustom] = useState(false); // rep picked "Newton Insurance — custom"
  const [dealName, setDealName] = useState('');
  const [dealOwnerEmail, setDealOwnerEmail] = useState('');
  const [memo, setMemo] = useState('');
  const [testMode, setTestMode] = useState(false);
  const [contractTerm, setContractTerm] = useState('annual'); // 'annual', '24-month', or 'month-to-month'
  const [dealType, setDealType] = useState('new_business'); // 'new_business' | 'expansion' | 'product_upsell' | 'phone_hardware_only'
  const [autoCreateAttio, setAutoCreateAttio] = useState(false);
  const [onboardingDiscount, setOnboardingDiscount] = useState('none'); // Presets, custom_percent, custom_amount, or legacy custom fee
  const [processingRates, setProcessingRates] = useState({ cardPercent: 2.75, achPercent: 1, perTransactionFee: 0.30 });
  const [onboardingDiscountValue, setOnboardingDiscountValue] = useState('');
  const [hardwareDiscountPercent, setHardwareDiscountPercent] = useState(0); // 5 | 10 | 20 — one-time hardware purchases only
  const [rampSchedule, setRampSchedule] = useState([{ month: 1, count: 1 }]);
  const [baaOnly] = useState(false);
  const [optOutDays, setOptOutDays] = useState(30);
  const [locations, setLocations] = useState([{ index: 1, name: '', street: '', city: '', state: '', zip: '', pms: '', phase: 1, practiceId: '', products: { phones: false, inbound: false, outbound: false, insuranceVerification: false, marketAttribution: false, digitalForms: false, pay: false } }]);

  // Stripe prices
  const [prices, setPrices] = useState([]);
  const [phoneHardwarePrices, setPhoneHardwarePrices] = useState([]);
  const [onetimeFeePrices, setOnetimeFeePrices] = useState([]);
  const [sdbProducts, setSdbProducts] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(true);

  // Phone hardware selection
  const [phoneHardwarePriceId, setPhoneHardwarePriceId] = useState('');
  const [phoneQuantity, setPhoneQuantity] = useState(1); // total phone count

  // Quote result
  const [shareableUrl, setShareableUrl] = useState('');
  const [contractPdfUrl, setContractPdfUrl] = useState('');
  const [currentQuoteId, setCurrentQuoteId] = useState(''); // set after create — form edits then PATCH this quote
  const [editLoading, setEditLoading] = useState(false); // true while hydrating an existing quote via /quoteBuilder?edit=<id>
  // Send-for-signature (Dropbox Sign) panel state
  const [sigName, setSigName] = useState('');
  const [sigTitle, setSigTitle] = useState('');
  const [sigEmail, setSigEmail] = useState('');
  const [sigSending, setSigSending] = useState(false);
  const [sigStatus, setSigStatus] = useState(''); // e.g. "Sent to x@y.com at 3:02 PM"
  const [sigError, setSigError] = useState('');
  const [showContractViewer, setShowContractViewer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchPrices = async () => {
    setPricesLoading(true);
    try {
      const res = await fetch(`${GTM_API_URL}/api/v2/prices`);
      const data = await res.json();
      if (data.success && data.data) {
        // Sort by unit_amount ascending
        const sorted = data.data.sort((a, b) => (a.unit_amount || 0) - (b.unit_amount || 0));
        setPrices(sorted);
        // Phone hardware prices
        if (data.phoneHardware) {
          setPhoneHardwarePrices(data.phoneHardware);
        }
        if (data.onetimeFees) {
          setOnetimeFeePrices(data.onetimeFees);
        }
        if (data.sdbProducts) {
          setSdbProducts(data.sdbProducts);
        }
      }
    } catch (err) {
      console.error('Error fetching prices:', err);
    } finally {
      setPricesLoading(false);
    }
  };

  // Fetch Stripe prices on mount
  useEffect(() => {
    fetchPrices();
    // eslint-disable-next-line
  }, []);

  // Auto-populate deal owner with the logged-in rep's email (still editable)
  useEffect(() => {
    let email = '';
    try {
      email = JSON.parse(localStorage.getItem('user'))?.email || '';
    } catch { /* corrupt or missing — fall through to supabase */ }
    if (email) {
      setDealOwnerEmail(prev => prev || email);
      return;
    }
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.email) setDealOwnerEmail(prev => prev || data.user.email);
    });
  }, []);

  // Edit-existing-quote flow: /quoteBuilder?edit=<quoteId> loads that quote
  // into the form and arms update-mode, so any teammate can edit any
  // unaccepted quote. Refreshing drops the param (App.js cleans the URL) —
  // re-enter from the Quotes list's Edit button.
  const loadQuoteForEditing = async (editId) => {
    setEditLoading(true);
    setError('');
    try {
        const res = await fetch(`${GTM_API_URL}/api/v2/quotes/${editId}`);
        const data = await res.json();
        if (!data.success || !data.quote) {
          setError(`Could not load quote ${editId} for editing`);
          return;
        }
        const q = data.quote;
        if (q.status === 'accepted') {
          setError(`Quote ${editId} has been accepted and paid — it can no longer be edited. Start a new quote instead.`);
          return;
        }
        setOrgName(q.organizationName || '');
        setFirstName(q.billingFirstName || '');
        setLastName(q.billingLastName || '');
        setEmail(q.billingEmail || '');
        setStripePriceIds(q.stripePriceIds && q.stripePriceIds.length > 0 ? q.stripePriceIds : (q.stripePriceId ? [q.stripePriceId] : []));
        setPriceDiscounts(q.priceDiscounts || {});
        setInsuranceApptsPerMonth(q.insuranceApptsPerMonth || '');
        const qty = q.quantity || (q.locations ? q.locations.length : 1) || 1;
        setQuantity(qty);
        if (q.locations && q.locations.length > 0) {
          setLocations(q.locations.map((l, i) => ({
            index: l.index || i + 1,
            name: l.name || '', street: l.street || '', city: l.city || '', state: l.state || '', zip: l.zip || '',
            pms: l.pms || '', phase: l.phase || 1, practiceId: l.practiceId || '',
            products: { phones: false, inbound: false, outbound: false, insuranceVerification: false, marketAttribution: false, digitalForms: false, pay: false, ...(l.products || {}) },
          })));
        }
        setBillingDeferralDays(q.billingDeferralDays || 0);
        setExistingStripeSubscriptionId(q.existingStripeSubscriptionId || '');
        // Prefer the stored multi-loc/bundle split; decompose legacy combined percents
        let ml = q.multiLocationDiscountPercent || 0;
        let bd = q.bundleDiscountPercent || 0;
        if (!ml && !bd && q.discountPercent) {
          const combo = { 5: [5, 0], 10: [10, 0], 15: [10, 5], 20: [10, 10] }[q.discountPercent];
          if (combo) { ml = combo[0]; bd = combo[1]; }
          else { ml = q.discountPercent; }
        }
        setMultiLocationDiscount(ml);
        setBundleDiscount(bd);
        setDealName(q.dealName || '');
        setDealOwnerEmail(prev => q.dealOwnerEmail || prev);
        setMemo(q.memo || '');
        setTestMode(q.test === true);
        setContractTerm(q.contractTerm === 'month-to-month' ? 'month-to-month' : q.contractTerm === '24-month' ? '24-month' : 'annual');
        setDealType(q.dealType || 'new_business');
        setOptOutDays(q.optOutDays || 30);
        const ob = q.onboardingDiscount === 'half' ? '50' : q.onboardingDiscount;
        setOnboardingDiscount(ob === 'custom' && q.onboardingDiscountType ? `custom_${q.onboardingDiscountType}` : (['none', '25', '50', 'waive', 'custom'].includes(ob) ? ob : 'none'));
        setProcessingRates(q.processingRates ?? { cardPercent: 2.75, achPercent: 1, perTransactionFee: 0.30 });
        setOnboardingDiscountValue(q.onboardingDiscountType ? (q.onboardingDiscountValue ?? '') : (q.onboardingFeeAmount ?? ''));
        setPhoneHardwarePriceId(q.phoneHardwarePriceId || '');
        setPhoneQuantity(q.phoneQuantity || 1);
        setHardwareDiscountPercent(q.hardwareDiscountPercent || 0);
        setRampSchedule(q.rolloutPhases && q.rolloutPhases.length > 0
          ? [...q.rolloutPhases].sort((a, b) => a.month - b.month).map(ph => ({ month: ph.month + 1, count: ph.locationIndices.length }))
          : [{ month: 1, count: qty }]);
        setSigName(q.signerName || [q.billingFirstName, q.billingLastName].filter(Boolean).join(' '));
        setSigTitle(q.signerTitle || '');
        setSigEmail(q.signerEmail || q.billingEmail || '');
        if (q.signatureSentAt) setSigStatus(`Previously sent to ${q.signerEmail || 'signer'} on ${new Date(q.signatureSentAt).toLocaleString()}`);
        setCurrentQuoteId(q.quoteId);
        setShareableUrl(`https://billing.joinnewton.com/quote/${q.quoteId}`);
        setContractPdfUrl(`${GTM_API_URL}/api/v2/quotes/${q.quoteId}/contract`);
        setSuccess(`Editing quote ${q.quoteId} (${q.status}) — submitting will update it in place`);
    } catch {
      setError('Could not load quote for editing');
    } finally {
      setEditLoading(false);
    }
  };

  // Edit-existing-quote flow: /quoteBuilder?edit=<quoteId> (e.g. from links)
  useEffect(() => {
    const editId = new URLSearchParams(window.location.search).get('edit');
    if (editId) loadQuoteForEditing(editId);
    // eslint-disable-next-line
  }, []);

  // Derived: is the rep building an SDB enterprise quote?
  const isSdbMode = isSdbCompanyName(orgName);

  // When the rep selects (or deselects) an SDB company, auto-configure the
  // quote for the SDB invoice track. Don't touch the org name itself; just
  // clear standard-flow selections that don't apply.
  useEffect(() => {
    if (isSdbMode) {
      // Drop any standard products the rep might have picked before switching
      // to SDB (they're invalid against the SDB price list).
      // Skip filtering until the SDB catalog has loaded — otherwise hydrating
      // an SDB quote on page load would wipe its product selection.
      if (sdbProducts.length > 0) {
        setStripePriceIds(prev =>
          prev.filter(id => sdbProducts.some(p => p.id === id))
        );
      }
      // Table 9A pricing is fixed by contract — no per-product discounts
      setPriceDiscounts({});
      setInsuranceApptsPerMonth('');
      setInsuranceCustom(false);
      setOnboardingDiscount('waive');
      // SDB doesn't sell phone hardware through this path.
      setPhoneHardwarePriceId('');
      setHardwareDiscountPercent(0);
    }
    // eslint-disable-next-line
  }, [isSdbMode, sdbProducts.length]);

  // Update locations when quantity changes
  const handleQuantityChange = useCallback((newQuantity) => {
    const qty = Math.max(1, parseInt(newQuantity) || 1);
    setQuantity(qty);

    setLocations(prev => {
      if (qty > prev.length) {
        // Add new location entries
        const newLocations = [...prev];
        for (let i = prev.length + 1; i <= qty; i++) {
          newLocations.push({ index: i, name: '', street: '', city: '', state: '', zip: '', pms: '', phase: 1, practiceId: '', products: { phones: false, inbound: false, outbound: false, insuranceVerification: false, marketAttribution: false, digitalForms: false, pay: false } });
        }
        return newLocations;
      } else if (qty < prev.length) {
        // Trim locations
        return prev.slice(0, qty);
      }
      return prev;
    });
  }, []);

  // Update a specific location field
  const updateLocation = (index, field, value) => {
    setLocations(prev =>
      prev.map(loc =>
        loc.index === index ? { ...loc, [field]: value } : loc
      )
    );
  };

  const addUpsellLocation = () => {
    setLocations(prev => {
      const nextIndex = prev.length > 0 ? Math.max(...prev.map(l => l.index)) + 1 : 1;
      return [...prev, { index: nextIndex, name: '', street: '', city: '', state: '', zip: '', pms: '', phase: 1, practiceId: '', products: { phones: false, inbound: false, outbound: false, insuranceVerification: false, marketAttribution: false, digitalForms: false, pay: false } }];
    });
  };

  const removeUpsellLocation = (index) => {
    setLocations(prev => prev.filter(loc => loc.index !== index));
  };

  const isProductUpsell = dealType === 'product_upsell';
  const isPhoneOnly = dealType === 'phone_hardware_only';


  // Submit quote
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setShareableUrl('');
    setContractPdfUrl('');

    if (isPhoneOnly) {
      if (!phoneHardwarePriceId || phoneHardwarePriceId === 'none') { setError('Phone hardware is required for a phone-only quote'); return; }
      if (!phoneQuantity || parseInt(phoneQuantity) < 1) { setError('Phone quantity must be at least 1'); return; }
    } else {
      if (!orgName) { setError('Organization name is required'); return; }
      if (!firstName) { setError('First name is required'); return; }
      if (!lastName) { setError('Last name is required'); return; }
      if (!email) { setError('Email is required'); return; }
      if (stripePriceIds.length === 0) { setError('At least one price is required'); return; }
      if (isProductUpsell) {
        const missingPracticeId = locations.find(loc => !loc.practiceId);
        if (missingPracticeId) { setError(`Practice #${missingPracticeId.index} is missing a Practice ID`); return; }
      } else {
        const missingPms = locations.find(loc => !loc.pms);
        if (missingPms) { setError(`Location #${missingPms.index} is missing a PMS selection`); return; }
      }
    }

    if (!isPhoneOnly && !isSdbMode) {
      const percentages = [multiLocationDiscount, bundleDiscount];
      if (percentages.some(value => value === '' || !Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > 100)) {
        setError('Enter a discount percentage from 0 to 100.');
        return;
      }
      if (Number(multiLocationDiscount) + Number(bundleDiscount) > 100) {
        setError('Multi-location and bundle discounts cannot total more than 100%.');
        return;
      }
    }

    if (!isPhoneOnly && !isProductUpsell && onboardingDiscount.startsWith('custom')) {
      const value = Number(onboardingDiscountValue);
      const max = onboardingDiscount === 'custom_percent' ? 100 : onboardingBaseFee;
      if (onboardingDiscountValue === '' || !Number.isFinite(value) || value < 0 || (onboardingDiscount !== 'custom' && value > max)) {
        setError(`Enter a valid implementation ${onboardingDiscount === 'custom_percent' ? 'percentage from 0 to 100' : 'dollar amount from 0 to ' + max}.`);
        return;
      }
    }
    setSubmitting(true);

    try {
      // Build rollout phases from ramp schedule
      const activePhases = rampSchedule.filter(r => (parseInt(r.count) || 0) > 0);
      const isPhased = activePhases.length > 1;
      let rolloutPhases = null;
      if (isProductUpsell) {
        rolloutPhases = buildUpsellRollout(rampSchedule, locations);
      } else if (isPhased) {
        let locIndex = 1;
        rolloutPhases = activePhases.map(r => {
          const count = parseInt(r.count) || 0;
          const indices = [];
          for (let i = 0; i < count; i++) {
            indices.push(locIndex++);
          }
          return {
            month: r.month - 1,
            interval: 'month',
            locationIndices: indices,
          };
        });
      }

      // Derive products from selected Stripe prices
      const derivedProducts = { phones: false, inbound: false, outbound: false, insuranceVerification: false, marketAttribution: false, digitalForms: false, pay: false };
      stripePriceIds.forEach(id => {
        const p = [...prices, ...sdbProducts].find(pr => pr.id === id);
        const mapped = p?.product_id ? PRODUCT_ID_MAP[p.product_id] : null;
        if (mapped) {
          (Array.isArray(mapped) ? mapped : [mapped]).forEach(key => {
            derivedProducts[key] = true;
          });
        }
      });
      const enrichedLocations = locations.map(loc => ({ ...loc, products: derivedProducts }));

      const body = isPhoneOnly ? {
        stripePriceIds: [],
        quantity: 1,
        dealType,
        phoneHardwarePriceId,
        phoneQuantity: parseInt(phoneQuantity) || 0,
        hardwareDiscountPercent: hardwarePct > 0 ? hardwarePct : undefined,
        onboardingDiscount: 'waive',
        createdBy: 'dashboard',
        test: testMode,
      } : {
        organizationName: orgName,
        billingFirstName: firstName,
        billingLastName: lastName,
        billingEmail: email,
        stripePriceIds,
        quantity: isProductUpsell ? locations.length : quantity,
        billingDeferralDays: parseInt(billingDeferralDays) || 0,
        existingStripeSubscriptionId: existingStripeSubscriptionId.trim() || null,
        discountPercent: discountPct,
        multiLocationDiscountPercent: mlPct,
        bundleDiscountPercent: bundlePct,
        discountAmount: discount,
        // Always send the map: {} clears stale per-product discounts on edit
        // (PATCH spreads over the doc), and sanitizes to none on create.
        priceDiscounts: activePriceDiscounts,
        processingRates: Object.fromEntries(Object.entries(processingRates).map(([key, value]) => [key, Number(value)])),
        insuranceApptsPerMonth: parseInt(insuranceApptsPerMonth) > 0 ? parseInt(insuranceApptsPerMonth) : null,
        locations: enrichedLocations,
        rolloutPhases,
        contractTerm,
        dealType,
        dealName: dealName || orgName,
        dealOwnerEmail: dealOwnerEmail || undefined,
        autoCreateAttio,
        phoneHardwarePriceId: phoneHardwarePriceId && phoneHardwarePriceId !== 'none' ? phoneHardwarePriceId : undefined,
        phoneQuantity: phoneHardwarePriceId && phoneHardwarePriceId !== 'none' ? (parseInt(phoneQuantity) || 0) : undefined,
        hardwareDiscountPercent: hardwarePct > 0 ? hardwarePct : undefined,
        onboardingDiscount: onboardingDiscount.startsWith('custom') ? 'custom' : onboardingDiscount,
        onboardingDiscountType: onboardingDiscount === 'custom_percent' ? 'percent' : onboardingDiscount === 'custom_amount' ? 'amount' : null,
        onboardingDiscountValue: onboardingDiscount.startsWith('custom_') ? Number(onboardingDiscountValue) : null,
        onboardingFeeAmount: onboardingDiscount.startsWith('custom') ? effectiveOnboardingFee : null,
        memo: memo.trim() || undefined,
        // SDB enterprise track: forces invoice-net30 billing + AP email
        ...(isSdbMode ? { billingMode: 'invoice_net30', invoiceEmail: SDB_INVOICE_EMAIL } : {}),
        createdBy: 'dashboard',
        test: testMode,
        baaOnly: isPhased ? baaOnly : false,
        optOutDays: (contractTerm === 'annual' || contractTerm === '24-month') ? (parseInt(optOutDays) || 30) : undefined,
      };

      const isUpdate = !!currentQuoteId;
      const requestOptions = {
          method: isUpdate ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        };
      const res = await secureFetch(
        `${EXPRESS_API_URL}/api/quotes${isUpdate ? `/${currentQuoteId}` : ''}`,
        requestOptions
      );

      const data = await res.json();

      if (data.success) {
        const quoteId = data.quote.quoteId;
        setCurrentQuoteId(quoteId);
        setShareableUrl(data.shareableUrl);
        setContractPdfUrl(data.contractPdfUrl || '');
        const defaultSignerName = `${firstName} ${lastName}`.trim();
        setSigName(prev => prev || defaultSignerName);
        setSigEmail(prev => prev || email);

        // Prepare the generated agreement immediately so the shareable link
        // always opens the correct PDF, even before the rep sends the email.
        if (!isUpdate && !isPhoneOnly) {
          const signatureRes = await secureFetch(`${EXPRESS_API_URL}/api/quotes/${quoteId}/send-signature`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              signerName: defaultSignerName,
              signerEmail: email,
              sendEmail: false,
            }),
          });
          const signatureData = await signatureRes.json();
          if (!signatureData.success) {
            setError(`Quote created, but its signing request could not be prepared: ${signatureData.error || 'Unknown error'}`);
            return;
          }
        }

        setSuccess(isUpdate
          ? `Quote updated: ${quoteId} — reopen the contract PDF to see the changes`
          : `Quote created: ${quoteId}`);
      } else {
        setError(data.error || (isUpdate ? 'Failed to update quote' : 'Failed to create quote'));
      }
    } catch (err) {
      setError(err.message || 'Error creating quote');
    } finally {
      setSubmitting(false);
    }
  };

  // Send the contract for signature via Dropbox Sign (emailed request)
  const handleSendSignature = async () => {
    setSigError('');
    if (!currentQuoteId) return;
    if (!sigName.trim()) { setSigError('Signer name is required'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(sigEmail.trim())) { setSigError('A valid signer email is required'); return; }
    setSigSending(true);
    try {
      const res = await secureFetch(`${EXPRESS_API_URL}/api/quotes/${currentQuoteId}/send-signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signerName: sigName.trim(),
          signerTitle: sigTitle.trim() || undefined,
          signerEmail: sigEmail.trim(),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSigStatus(`Sent to ${data.sentTo} at ${new Date().toLocaleTimeString()}${data.testMode ? ' (Dropbox Sign test mode)' : ''}`);
      } else {
        setSigError(data.error || 'Failed to send for signature');
      }
    } catch (err) {
      setSigError(err.message || 'Failed to send for signature');
    } finally {
      setSigSending(false);
    }
  };

  // Copy URL to clipboard
  const copyUrl = () => {
    navigator.clipboard.writeText(shareableUrl);
  };

  // Resolve selected price IDs across both catalogs so SDB picks render correctly.
  const allPricesById = [...prices, ...sdbProducts];
  const selectedPrices = stripePriceIds.map(id => allPricesById.find(p => p.id === id)).filter(Boolean);
  const subtotal = selectedPrices.reduce((sum, p) => sum + (p.unit_amount / 100), 0);
  // Per-product discounts: only count entries for currently-selected products
  const activePriceDiscounts = Object.fromEntries(
    Object.entries(priceDiscounts).filter(([pid, amt]) => stripePriceIds.includes(pid) && amt > 0)
  );
  const productDiscountPerLoc = Object.values(activePriceDiscounts).reduce((sum, v) => sum + v, 0);
  // The selected Newton Insurance price, if any — drives the Zuub cost calculator.
  const insurancePrice = isSdbMode ? null : (selectedPrices.find(isInsurancePrice) || null);
  // "Newton Insurance — custom" rides on the highest-list insurance price so any
  // custom amount below it is expressible as a per-product discount.
  const insuranceBasePrice = isSdbMode ? null : (
    prices.filter(isInsurancePrice).sort((a, b) => b.unit_amount - a.unit_amount)[0] || null
  );
  // Custom mode: the rep picked the custom row, or the loaded quote already
  // carries a custom insurance price / appointment volume.
  const showInsuranceCalculator = !!insurancePrice && (
    insuranceCustom || (priceDiscounts[insurancePrice.id] || 0) > 0 || !!insuranceApptsPerMonth
  );
  const effectiveSubtotal = Math.max(0, subtotal - productDiscountPerLoc);
  const locationCount = isProductUpsell ? locations.length : Math.max(1, parseInt(quantity) || 1);
  const mlPct = Number(multiLocationDiscount) || 0;
  const bundlePct = Number(bundleDiscount) || 0;
  const discountPct = Math.min(100, Math.round((mlPct + bundlePct) * 100) / 100);
  // Percent discount applies after product discounts — mirrors Stripe, where the
  // % coupon sits on top of the already-discounted prices
  const monthlyBeforeDiscount = effectiveSubtotal * locationCount;
  const discount = monthlyBeforeDiscount * (discountPct / 100);
  const monthlyTotal = monthlyBeforeDiscount - discount;

  // Phone hardware
  const selectedPhoneHardware = phoneHardwarePriceId !== 'none' ? phoneHardwarePrices.find(p => p.id === phoneHardwarePriceId) : null;
  const isPhonePurchase = selectedPhoneHardware && !selectedPhoneHardware.recurring;
  const isPhoneLease = selectedPhoneHardware && selectedPhoneHardware.recurring;
  // phoneQuantity is the total phone count — no per-location multiplication
  const totalPhones = parseInt(phoneQuantity) || 0;
  // Hardware % discount (5/10/20) applies to one-time purchases only, never leases
  const hardwarePct = isPhonePurchase ? (parseInt(hardwareDiscountPercent) || 0) : 0;
  const phoneHardwareTotal = selectedPhoneHardware
    ? (selectedPhoneHardware.unit_amount / 100) * totalPhones * (1 - hardwarePct / 100)
    : 0;

  // Implementation fee: $1K base + $1K per full 5 locations
  const onboardingFee = onetimeFeePrices.length > 0 ? onetimeFeePrices.sort((a, b) => b.unit_amount - a.unit_amount)[0] : null;
  const onboardingBaseFee = 1000 * (1 + Math.floor(locationCount / 5));
  const obPctOff = onboardingDiscount === 'custom_percent' ? Number(onboardingDiscountValue) || 0 : onboardingDiscount === '25' ? 25 : onboardingDiscount === '50' ? 50 : 0;
  const effectiveOnboardingFee = Math.max(0, Math.round((onboardingDiscount === 'waive' ? 0 : onboardingDiscount === 'custom' ? Number(onboardingDiscountValue) || 0 : onboardingDiscount === 'custom_amount' ? onboardingBaseFee - (Number(onboardingDiscountValue) || 0) : onboardingBaseFee * (1 - obPctOff / 100)) * 100) / 100);
  const showOnboardingInSummary = !isProductUpsell && onboardingDiscount !== 'waive';

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '30px 20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 6, color: '#1a1a1a' }}>Quote Builder</h1>
      <p style={{ color: '#666', marginBottom: 20, fontSize: 14 }}>Generate and manage customer quotes.</p>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', marginBottom: 30 }}>
        {[
          { id: 'builder', label: 'Build Quote' },
          { id: 'quotes', label: 'Quote List' },
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSubTab(tab.id)}
            style={{
              padding: '10px 18px',
              border: 'none',
              borderBottom: activeSubTab === tab.id ? '2px solid #1E6434' : '2px solid transparent',
              background: 'transparent',
              color: activeSubTab === tab.id ? '#1E6434' : '#6b7280',
              fontSize: 14,
              fontWeight: activeSubTab === tab.id ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeSubTab === 'builder' && (
      <>
      {/* SDB enterprise mode banner — fires when the rep picks SDB as the company */}
      {isSdbMode && (
        <div style={{ marginBottom: 24, padding: '14px 18px', borderRadius: 10, border: '1px solid #fcd34d', background: 'linear-gradient(90deg, #fef3c7 0%, #fde68a 100%)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>🏢</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#78350f' }}>SDB Enterprise Mode</div>
            <div style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>
              Catalog filtered to SDB products. Onboarding waived. Billing set to{' '}
              <strong>net-30 invoices to {SDB_INVOICE_EMAIL}</strong> per the SDB master agreement. Customer will sign the SDB Order Form, not the standard MSA + BAA.
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start' }}>
      {/* Left column — form */}
      <div style={{ flex: 1, minWidth: 0 }}>
      <form onSubmit={handleSubmit}>
        {/* Deal Type — always at top so reps can switch into phone-only mode first */}
        <Section title="Deal Type">
          <FormRow>
            <Field label="Deal Type">
              <select value={dealType} onChange={e => {
                const val = e.target.value;
                setDealType(val);
                if (val === 'product_upsell' || val === 'phone_hardware_only') setOnboardingDiscount('waive');
                if (val === 'phone_hardware_only') {
                  setStripePriceIds([]);
                  setQuantity(1);
                }
              }}>
                <option value="new_business">New Business</option>
                <option value="expansion">Expansion</option>
                <option value="product_upsell">Product Upsell</option>
                <option value="phone_hardware_only">Phone Hardware Only</option>
              </select>
            </Field>
            <div />
          </FormRow>
        </Section>

        {/* Deal Info */}
        {!isPhoneOnly && (
        <Section title="Deal Information">
          <FormRow>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>
                Organization Name<span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>
              </label>
              <AttioAutocomplete
                value={orgName}
                onChange={setOrgName}
                placeholder="Acme Dental Group"
                endpoint="companies"
                inputStyle={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>
                Deal Name (Attio)
              </label>
              <AttioAutocomplete
                value={dealName}
                onChange={setDealName}
                placeholder="Defaults to org name if empty"
                endpoint="deals"
                inputStyle={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </FormRow>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 16px', borderRadius: 8, border: autoCreateAttio ? '1px solid #a7f3d0' : '1px solid #e5e7eb', background: autoCreateAttio ? '#ecfdf5' : '#fafafa' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: autoCreateAttio ? '#047857' : '#374151' }}>
              <input
                type="checkbox"
                checked={autoCreateAttio}
                onChange={e => setAutoCreateAttio(e.target.checked)}
                style={{ accentColor: '#1E6434', width: 16, height: 16 }}
              />
              Auto-create Attio records
            </label>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {autoCreateAttio ? 'Will create Company and Deal in Attio if they don\'t exist' : 'Company and Deal must already exist in Attio if not selected'}
            </span>
          </div>
          <FormRow>
            <Field label="Deal Owner Email">
              <input type="email" value={dealOwnerEmail} onChange={e => setDealOwnerEmail(e.target.value)} placeholder="rep@newton.ai" />
            </Field>
            <div />
          </FormRow>
        </Section>
        )}

        {/* Customer Info */}
        {!isPhoneOnly && (
        <Section title="Customer Information">
          <FormRow>
            <Field label="First Name" required>
              <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="John" />
            </Field>
            <Field label="Last Name" required>
              <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Doe" />
            </Field>
          </FormRow>
          <FormRow>
            <Field label="Email" required>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@acmedental.com" />
            </Field>
          </FormRow>
        </Section>
        )}

        {/* Terms */}
        {!isPhoneOnly && (
        <Section title="Terms">
          <FormRow>
            {!isProductUpsell && !isPhoneOnly && (
              <Field label="Number of Locations" required>
                <input type="number" min="1" value={quantity} onChange={e => setQuantity(e.target.value)} onBlur={e => handleQuantityChange(e.target.value)} />
              </Field>
            )}
            {!isProductUpsell && !isPhoneOnly && (
              <Field label="Contract Term">
                <select value={contractTerm} onChange={e => setContractTerm(e.target.value)}>
                  <option value="annual">Annual (12 months)</option>
                  <option value="24-month">24 months</option>
                  <option value="month-to-month">Month-to-Month</option>
                </select>
              </Field>
            )}
          </FormRow>
          {!isProductUpsell && !isPhoneOnly && !isSdbMode && (contractTerm === 'annual' || contractTerm === '24-month') && (
            <FormRow>
              <Field label="Opt-Out Period (days)">
                <input type="number" min="0" value={optOutDays} onChange={e => setOptOutDays(e.target.value)} placeholder="30" />
              </Field>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  Customer can terminate within {optOutDays || 30} days of signing
                </span>
              </div>
            </FormRow>
          )}
          {!isPhoneOnly && !isSdbMode && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 14, background: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Ramp Schedule</span>
              <span style={{ fontSize: 12, color: (() => {
                const assigned = rampSchedule.reduce((sum, r) => sum + (parseInt(r.count) || 0), 0);
                const total = locationCount;
                return assigned === total ? '#16a34a' : '#dc2626';
              })() }}>
                {rampSchedule.reduce((sum, r) => sum + (parseInt(r.count) || 0), 0)} / {locationCount} locations assigned
              </span>
            </div>
            {isProductUpsell && (
              <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 12px' }}>
                Add a month to phase billing for the selected products. Practices are assigned in the order listed below.
                Newly added products enter Onboarding after payment; this schedule controls billing.
              </p>
            )}
            {rampSchedule.map((row, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', minWidth: 70 }}>Month {row.month}</span>
                <input
                  type="number"
                  min="0"
                  value={row.count}
                  onChange={e => {
                    const val = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                    setRampSchedule(prev => prev.map((r, j) => j === i ? { ...r, count: val } : r));
                  }}
                  onBlur={e => {
                    if (i === 0 && (!e.target.value || parseInt(e.target.value) < 1)) {
                      setRampSchedule(prev => prev.map((r, j) => j === 0 ? { ...r, count: 1 } : r));
                    } else if (!e.target.value) {
                      setRampSchedule(prev => prev.map((r, j) => j === i ? { ...r, count: 0 } : r));
                    }
                  }}
                  placeholder="0"
                  style={{ width: 70, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, textAlign: 'center', boxSizing: 'border-box' }}
                />
                <span style={{ fontSize: 12, color: '#9ca3af' }}>locations</span>
                {i > 0 && (
                  <button
                    type="button"
                    onClick={() => setRampSchedule(prev => prev.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, padding: '2px 6px' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                    onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => setRampSchedule(prev => [...prev, { month: Math.max(...prev.map(row => row.month)) + 1, count: 0 }])}
              style={{ fontSize: 13, color: '#1E6434', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '4px 0', marginTop: 4 }}
            >
              + Add Month
            </button>
          </div>
          )}
        </Section>
        )}

        {/* Products */}
        <Section title="Products">
          {!isPhoneOnly && (
          <FormRow>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>
                Products<span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>
              </label>
              {/* Selected tags */}
              {stripePriceIds.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {stripePriceIds.map(id => {
                    const price = allPricesById.find(p => p.id === id);
                    const inCustomMode = isInsurancePrice(price) && showInsuranceCalculator;
                    const hasCustomPrice = inCustomMode && (priceDiscounts[id] || 0) > 0;
                    const shownAmount = price ? Math.max(0, (price.unit_amount / 100) - (hasCustomPrice ? priceDiscounts[id] : 0)) : 0;
                    const name = price ? (price.product_name || price.nickname || '') : '';
                    const label = !price ? id
                      : inCustomMode && !hasCustomPrice ? `${name} — custom`
                      : `${name} — $${shownAmount.toLocaleString()}/${price.recurring?.interval || 'month'}${hasCustomPrice ? ' (custom)' : ''}`;
                    return (
                      <span key={id} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6,
                        padding: '4px 10px', fontSize: 13, color: '#047857', fontWeight: 500,
                      }}>
                        {label}
                        {!isSdbMode && !inCustomMode && (
                          <select
                            value={priceDiscounts[id] || ''}
                            onChange={e => {
                              const amt = parseInt(e.target.value) || 0;
                              setPriceDiscounts(prev => {
                                const next = { ...prev };
                                if (amt > 0) next[id] = amt; else delete next[id];
                                return next;
                              });
                            }}
                            style={{
                              fontSize: 11, padding: '2px 4px', border: '1px solid #a7f3d0',
                              borderRadius: 4, color: '#047857', background: '#fff', cursor: 'pointer',
                            }}
                            title="Price-level discount per location per month"
                          >
                            <option value="">No discount</option>
                            {PRICE_DISCOUNT_OPTIONS.map(amt => (
                              <option key={amt} value={amt}>${amt}/mo off</option>
                            ))}
                          </select>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const next = stripePriceIds.filter(p => p !== id);
                            setStripePriceIds(next);
                            if (isInsurancePrice(price)) {
                              setInsuranceCustom(false);
                              setInsuranceApptsPerMonth('');
                            }
                            setPriceDiscounts(prev => {
                              const rest = { ...prev };
                              delete rest[id];
                              return rest;
                            });
                          }}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#6b7280', fontSize: 15, lineHeight: 1, padding: 0,
                            display: 'flex', alignItems: 'center',
                          }}
                          onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                          onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              {/* Dropdown to add */}
              {pricesLoading ? (
                <select disabled style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}>
                  <option>Loading prices...</option>
                </select>
              ) : (
                <select
                  value=""
                  onChange={e => {
                    const val = e.target.value;
                    if (val === '__insurance_custom__') {
                      if (insuranceBasePrice && !stripePriceIds.includes(insuranceBasePrice.id)) {
                        setStripePriceIds(prev => [...prev, insuranceBasePrice.id]);
                      }
                      setInsuranceCustom(true);
                      return;
                    }
                    if (val === '__all__') {
                      setStripePriceIds((isSdbMode ? sdbProducts : prices).map(p => p.id));
                      return;
                    }
                    if (val && !stripePriceIds.includes(val)) {
                      setStripePriceIds(prev => [...prev, val]);
                    }
                  }}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                >
                  <option value="">Select a product to add...</option>
                  {(isSdbMode ? sdbProducts : prices).some(p => !stripePriceIds.includes(p.id)) && (
                    <option value="__all__">Select all products</option>
                  )}
                  {!isSdbMode && insuranceBasePrice && !insurancePrice && (
                    <option value="__insurance_custom__">Newton Insurance — custom</option>
                  )}
                  {(isSdbMode ? sdbProducts : prices).filter(p => !stripePriceIds.includes(p.id)).map(price => {
                    const label = price.product_name || price.nickname || '';
                    return (
                      <option key={price.id} value={price.id}>
                        {label ? `${label} — ` : ''}${(price.unit_amount / 100).toLocaleString()}/{price.recurring?.interval || 'month'}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
          </FormRow>
          )}
          {!isPhoneOnly && showInsuranceCalculator && (
            <InsuranceCostCalculator
              price={insurancePrice}
              locationCount={isProductUpsell ? locations.length : locationCount}
              apptsPerMonth={insuranceApptsPerMonth}
              onApptsChange={setInsuranceApptsPerMonth}
              discount={priceDiscounts[insurancePrice.id] || 0}
              onDiscountChange={amt => {
                setPriceDiscounts(prev => {
                  const next = { ...prev };
                  if (amt > 0) next[insurancePrice.id] = amt; else delete next[insurancePrice.id];
                  return next;
                });
              }}
            />
          )}
        </Section>

        {!isPhoneOnly && selectedPrices.some(p => PRODUCT_ID_MAP[p.product_id] === 'pay' || /pay/i.test(p.product_name || '')) && (
          <Section title="Payment Processing Rates">
            <FormRow>
              {[
                ['cardPercent', 'Card rate (%)'],
                ['achPercent', 'ACH rate (%)'],
                ['perTransactionFee', 'Per-transaction fee ($)'],
              ].map(([key, label]) => (
                <Field key={key} label={label} required>
                  <input type="number" min="0" max={key === 'perTransactionFee' ? undefined : 100} step="0.01" required
                    value={processingRates[key]} disabled={!!currentQuoteId}
                    onChange={e => setProcessingRates(prev => ({ ...prev, [key]: e.target.value }))} />
                </Field>
              ))}
            </FormRow>
            <p>{currentQuoteId ? "Payment processing rates are locked after the quote is created. Create a new quote to use different rates." : "These rates appear in the agreement. The per-transaction fee applies to both card and ACH payments."}</p>
          </Section>
        )}

        {/* Hardware — standalone section, $139 purchase option only. Hidden for SDB. */}
        {!isSdbMode && (
        <Section title="Hardware">
          <FormRow>
            <Field label="Phone Option" required={isPhoneOnly}>
              <select value={phoneHardwarePriceId} onChange={e => { setPhoneHardwarePriceId(e.target.value); if (e.target.value === 'none' || e.target.value === '') setHardwareDiscountPercent(0); }}>
                <option value="">Select an option...</option>
                <option value="none">New phones not needed</option>
                {phoneHardwarePrices.filter(hp => hp.unit_amount === 13900).map(hp => (
                  <option key={hp.id} value={hp.id}>
                    {hp.product_name || hp.nickname} — ${(hp.unit_amount / 100).toLocaleString()}{hp.recurring ? `/${hp.recurring.interval}` : ' one-time'}
                  </option>
                ))}
              </select>
            </Field>
            {phoneHardwarePriceId && phoneHardwarePriceId !== 'none' && (
            <Field label="Number of Phones" required>
              <input type="number" min="1" value={phoneQuantity} onChange={e => setPhoneQuantity(e.target.value === '' ? '' : parseInt(e.target.value) || '')} onBlur={e => { if (!e.target.value || parseInt(e.target.value) < 1) setPhoneQuantity(1); }} />
            </Field>
            )}
          </FormRow>
          {/* Hardware discount — one-time purchases only */}
          {isPhonePurchase && (
            <FormRow>
              <Field label="Hardware Discount (one-time cost)">
                <select value={hardwareDiscountPercent} onChange={e => setHardwareDiscountPercent(parseInt(e.target.value) || 0)}>
                  <option value={0}>No discount</option>
                  <option value={5}>5% off</option>
                  <option value={10}>10% off</option>
                  <option value={20}>20% off</option>
                </select>
              </Field>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  {hardwareDiscountPercent > 0
                    ? `${hardwareDiscountPercent}% off the one-time hardware total`
                    : 'Applies to the one-time hardware total'}
                </span>
              </div>
            </FormRow>
          )}
        </Section>
        )}

        {/* Discounts and Incentives — hidden for SDB (Table 9A pricing is fixed by contract) */}
        {!isPhoneOnly && !isSdbMode && (
        <Section title="Discounts and Incentives">
          <FormRow>
            <Field label="Multi-Location Discount">
              <DiscountPercentageField label="Multi-Location Discount" value={multiLocationDiscount} onChange={setMultiLocationDiscount} />
            </Field>
            <Field label="Bundle Discount">
              <DiscountPercentageField label="Bundle Discount" value={bundleDiscount} onChange={setBundleDiscount} />
            </Field>
            <Field label="Billing Deferral (days)">
              <input type="number" min="0" value={billingDeferralDays} onChange={e => setBillingDeferralDays(e.target.value)} placeholder="0" />
            </Field>
          </FormRow>
          {!isProductUpsell && (
          <FormRow>
            <Field label="Implementation Fee">
              <select value={onboardingDiscount} disabled={isSdbMode} onChange={e => setOnboardingDiscount(e.target.value)} title={isSdbMode ? 'Locked: SDB enterprise quotes always waive onboarding per the master agreement.' : undefined}>
                <option value="none">Full price (${onboardingBaseFee.toLocaleString()})</option>
                <option value="25">25% off (${(onboardingBaseFee * 0.75).toLocaleString()})</option>
                <option value="50">50% off (${(onboardingBaseFee * 0.5).toLocaleString()})</option>
                <option value="waive">Waive entirely</option>
                <option value="custom_percent">Custom percentage off</option>
                <option value="custom_amount">Custom dollar amount off</option>
                {onboardingDiscount === 'custom' && <option value="custom">Custom fee (legacy)</option>}
              </select>
            </Field>
            {onboardingDiscount.startsWith('custom') && (
              <div>
                <Field label={onboardingDiscount === 'custom_percent' ? 'Implementation discount (%)' : onboardingDiscount === 'custom_amount' ? 'Implementation discount ($ off)' : 'Implementation fee ($)'}>
                  <input type="number" min="0" max={onboardingDiscount === 'custom_percent' ? 100 : onboardingDiscount === 'custom_amount' ? onboardingBaseFee : undefined} step="0.01" required disabled={isSdbMode} value={onboardingDiscountValue} onChange={e => setOnboardingDiscountValue(e.target.value)} />
                </Field>
                <span style={{ fontSize: 12, color: '#6b7280' }}>Final fee: ${effectiveOnboardingFee.toLocaleString()}</span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                $1K base + $1K per full 5 locations ({locationCount} location{locationCount !== 1 ? 's' : ''} → ${onboardingBaseFee.toLocaleString()})
              </span>
            </div>
          </FormRow>
          )}
        </Section>
        )}

        {/* Locations */}
        {isPhoneOnly ? null : isProductUpsell ? (
        <Section title={`Practices (${locations.length})`}>
          <div style={{ overflowX: 'auto' }}>
            {locations.map(loc => (
              <div key={loc.index} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12, background: '#fafafa' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', minWidth: 28 }}>#{loc.index}</span>
                  <input
                    type="text"
                    value={loc.practiceId || ''}
                    onChange={e => updateLocation(loc.index, 'practiceId', e.target.value)}
                    placeholder="practice-1234567890"
                    style={{ ...tableInputStyle, flex: 1, fontFamily: 'monospace' }}
                  />
                  <input
                    type="text"
                    value={loc.name}
                    onChange={e => updateLocation(loc.index, 'name', e.target.value)}
                    placeholder="Location name (optional)"
                    style={{ ...tableInputStyle, flex: 1 }}
                  />
                  {locations.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeUpsellLocation(loc.index)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18, padding: '2px 6px', lineHeight: 1 }}
                      onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                      onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addUpsellLocation}
            style={{ fontSize: 13, color: '#1E6434', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '4px 0' }}
          >
            + Add Practice
          </button>
        </Section>
        ) : (
        <Section title={`Locations (${quantity})`}>
          <ApplyToAll locations={locations} setLocations={setLocations} />
          <div style={{ overflowX: 'auto' }}>
            {locations.map(loc => (
              <div key={loc.index} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12, background: '#fafafa' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', minWidth: 28 }}>#{loc.index}</span>
                  <input
                    type="text"
                    value={loc.name}
                    onChange={e => updateLocation(loc.index, 'name', e.target.value)}
                    placeholder="Location name"
                    style={{ ...tableInputStyle, flex: 1 }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                  <input type="text" value={loc.street} onChange={e => updateLocation(loc.index, 'street', e.target.value)} placeholder="Street" style={{ ...tableInputStyle, flex: 2 }} />
                  <input type="text" value={loc.city} onChange={e => updateLocation(loc.index, 'city', e.target.value)} placeholder="City" style={{ ...tableInputStyle, flex: 1 }} />
                  <input type="text" value={loc.state} onChange={e => updateLocation(loc.index, 'state', e.target.value)} placeholder="ST" style={{ ...tableInputStyle, width: 50 }} />
                  <input type="text" value={loc.zip} onChange={e => updateLocation(loc.index, 'zip', e.target.value)} placeholder="Zip" style={{ ...tableInputStyle, width: 70 }} />
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 180 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>PMS</label>
                    <select value={loc.pms} onChange={e => updateLocation(loc.index, 'pms', e.target.value)} style={{ ...tableInputStyle, width: '100%' }}>
                      <option value="">Select PMS...</option>
                      <option value="Curve">Curve</option>
                      <option value="OpenDental">OpenDental</option>
                      <option value="Dentrix">Dentrix</option>
                      <option value="Eaglesoft">Eaglesoft</option>
                      <option value="Ascend">Ascend</option>
                      <option value="Denticon">Denticon</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
        )}

        {/* Notes / Memo — customer-visible free text */}
        <Section title="Notes">
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>
              Memo <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional — shown to the customer on their quote page)</span>
            </label>
            <textarea
              value={memo}
              onChange={e => setMemo(e.target.value)}
              placeholder="e.g. 'Pricing locked through end of Q4 per our 11/4 call.'"
              rows={3}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', resize: 'vertical' }}
            />
          </div>
        </Section>

        {!isPhoneOnly && (
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', marginBottom: 8 }}>Existing Stripe subscription ID (optional)</label>
            <input aria-label="Existing Stripe subscription ID" value={existingStripeSubscriptionId}
              onChange={e => setExistingStripeSubscriptionId(e.target.value)} placeholder="sub_..."
              style={{ width: '100%', padding: 12, border: '1px solid #d1d5db', borderRadius: 6 }} />
            <p style={{ color: '#6b7280', fontSize: 13 }}>Add this order to an existing subscription instead of collecting payment details. Leave blank for normal checkout.</p>
          </div>
        )}
        {/* Test Mode Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, padding: '12px 16px', borderRadius: 8, border: testMode ? '1px solid #f59e0b' : '1px solid #e5e7eb', background: testMode ? '#fffbeb' : '#fafafa' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: testMode ? '#92400e' : '#374151' }}>
            <input
              type="checkbox"
              checked={testMode}
              onChange={e => setTestMode(e.target.checked)}
              style={{ accentColor: '#f59e0b', width: 16, height: 16 }}
            />
            Test Mode
          </label>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {testMode ? 'Quote will use Stripe test keys — no real charges' : 'Uses live Stripe keys'}
          </span>
        </div>

        {/* Error / Success */}
        {editLoading && <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#2563eb', fontSize: 14 }}>Loading quote for editing...</div>}
      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#dc2626', fontSize: 14 }}>{error}</div>}
        {success && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#16a34a', fontSize: 14 }}>{success}</div>}

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting || editLoading}
          style={{
            background: submitting ? '#9ca3af' : '#1E6434',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '12px 32px',
            fontSize: 15,
            fontWeight: 600,
            cursor: submitting ? 'not-allowed' : 'pointer',
            width: '100%',
            marginBottom: 20,
          }}
        >
          {submitting ? (currentQuoteId ? 'Updating Quote...' : 'Creating Quote...') : (currentQuoteId ? 'Update Quote & Regenerate Contract' : 'Generate Quote')}
        </button>

      {/* Shareable URL */}
      {shareableUrl && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20 }}>
          <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 8 }}>Shareable Quote Link</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              readOnly
              value={shareableUrl}
              style={{ flex: 1, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, background: '#fff' }}
            />
            <button
              type="button"
              onClick={copyUrl}
              style={{ padding: '10px 20px', background: '#1E6434', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}
            >
              Copy
            </button>
          </div>
          {contractPdfUrl && (
            <div style={{ marginTop: 12 }}>
              <label style={{ fontWeight: 700, fontSize: 13, color: '#0B4426', display: 'block', marginBottom: 8 }}>Step 1 · Review the Contract (MSA + Order Form + BAA)</label>
              <button
                type="button"
                onClick={() => setShowContractViewer(true)}
                style={{ padding: '10px 20px', background: '#1E6434', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                Preview Contract
              </button>
              <a
                href={contractPdfUrl}
                target="_blank"
                rel="noreferrer"
                style={{ display: 'inline-block', padding: '10px 20px', background: '#fff', color: '#1E6434', border: '1px solid #1E6434', borderRadius: 6, fontWeight: 600, fontSize: 13, textDecoration: 'none', marginLeft: 8 }}
              >
                Open in New Tab
              </a>
              <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 10 }}>
                Renders live from the saved quote — edit the form, hit "Update Quote", then reopen.
              </span>
            </div>
          )}
          {currentQuoteId && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e2e8f0' }}>
              <label style={{ fontWeight: 700, fontSize: 13, color: '#0B4426', display: 'block', marginBottom: 4 }}>Step 2 · Send for Signature — emails from onboarding@joinnewton.com</label>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                Sends a Newton email linking to the quote summary page, where the signer reviews terms, signs the MSA, and sets up payment. Their name and title appear in the signature blocks.
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  type="text"
                  value={sigName}
                  onChange={e => setSigName(e.target.value)}
                  placeholder="Signer full name"
                  style={{ flex: 1.2, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
                <input
                  type="text"
                  value={sigTitle}
                  onChange={e => setSigTitle(e.target.value)}
                  placeholder="Title (e.g. Owner, DDS)"
                  style={{ flex: 1, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="email"
                  value={sigEmail}
                  onChange={e => setSigEmail(e.target.value)}
                  placeholder="signer@practice.com"
                  style={{ flex: 1, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
                <button
                  type="button"
                  onClick={handleSendSignature}
                  disabled={sigSending}
                  style={{
                    padding: '9px 20px', background: sigSending ? '#9ca3af' : '#0B4426', color: '#fff',
                    border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13,
                    cursor: sigSending ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  {sigSending ? 'Sending...' : (sigStatus ? 'Resend' : 'Send for Signature')}
                </button>
              </div>
              {sigStatus && <div style={{ marginTop: 8, fontSize: 12, color: '#047857', fontWeight: 600 }}>✓ {sigStatus}</div>}
              {sigError && <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>{sigError}</div>}
              {testMode && !sigStatus && <div style={{ marginTop: 8, fontSize: 11, color: '#92400e' }}>Test Mode quote — the signature request is sent in Dropbox Sign test mode (watermarked, not legally binding).</div>}
            </div>
          )}
          {currentQuoteId && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
              <button
                type="button"
                onClick={() => {
                  setCurrentQuoteId('');
                  setShareableUrl('');
                  setContractPdfUrl('');
                  setSuccess('');
                  setError('');
                }}
                style={{ padding: '8px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
              >
                Start New Quote
              </button>
              <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 10 }}>
                Keeps the form values but the next submit creates a fresh quote.
              </span>
            </div>
          )}
        </div>
      )}
      </form>
      </div>

      {/* Right column — Quote Summary */}
      <div style={{ width: 320, flexShrink: 0, position: 'sticky', top: 30 }}>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
          <div style={{ background: '#1E6434', padding: '14px 20px' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>Quote Summary</h3>
          </div>
          <div style={{ padding: '20px' }}>
            {/* Customer */}
            {orgName && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Customer</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{orgName}</div>
                {(firstName || lastName) && <div style={{ fontSize: 13, color: '#6b7280' }}>{firstName} {lastName}</div>}
              </div>
            )}

            {/* Memo preview */}
            {memo.trim() && (
              <div style={{ marginBottom: 16, padding: '10px 12px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Memo</div>
                <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{memo.trim()}</div>
              </div>
            )}

            {/* Products */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Products</div>
              {selectedPrices.length === 0 ? (
                <div style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>
                  {isPhoneOnly ? 'Phone hardware only (one-time)' : 'No products selected'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selectedPrices.map(price => {
                    const lineDiscount = activePriceDiscounts[price.id] || 0;
                    return (
                      <div key={price.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontSize: 13, color: '#374151' }}>
                          {price.product_name || price.nickname || 'Product'}
                          {lineDiscount > 0 && <span style={{ fontSize: 11, color: '#16a34a', marginLeft: 4 }}>({isInsurancePrice(price) && showInsuranceCalculator ? 'custom' : `-$${lineDiscount}/mo`})</span>}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
                          {lineDiscount > 0 && (
                            <span style={{ color: '#9ca3af', textDecoration: 'line-through', fontWeight: 400, marginRight: 5 }}>
                              ${(price.unit_amount / 100).toLocaleString()}
                            </span>
                          )}
                          ${Math.max(0, (price.unit_amount / 100) - lineDiscount).toLocaleString()}/{price.recurring?.interval || 'mo'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Divider */}
            {!isPhoneOnly && <div style={{ borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />}

            {/* Qty */}
            {!isPhoneOnly && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#6b7280' }}>Locations</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{locationCount}</span>
              </div>
            )}

            {/* Subtotal per location */}
            {selectedPrices.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#6b7280' }}>
                  Subtotal / location
                  {productDiscountPerLoc > 0 && <span style={{ fontSize: 11, color: '#16a34a', marginLeft: 4 }}>(after product discounts)</span>}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>${effectiveSubtotal.toLocaleString()}/mo</span>
              </div>
            )}

            {/* Discounts — itemized by type */}
            {mlPct > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#6b7280' }}>Multi-location discount ({mlPct}%)</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}>-${(monthlyBeforeDiscount * (mlPct / 100)).toLocaleString()}/mo</span>
              </div>
            )}
            {bundlePct > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#6b7280' }}>Bundle discount ({bundlePct}%)</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}>-${(monthlyBeforeDiscount * (bundlePct / 100)).toLocaleString()}/mo</span>
              </div>
            )}

            {/* Phone lease — add to recurring */}
            {isPhoneLease && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#6b7280' }}>Phone lease ({totalPhones} phones)</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>${phoneHardwareTotal.toLocaleString()}/{selectedPhoneHardware.recurring.interval}</span>
              </div>
            )}

            {/* Monthly Total — hidden for phone-only quotes (no recurring) */}
            {!isPhoneOnly && (
              <>
                <div style={{ borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>Monthly Total</span>
                  <span style={{ fontSize: 20, fontWeight: 700, color: '#1E6434' }}>
                    {selectedPrices.length > 0 ? `$${Math.max(0, monthlyTotal + (isPhoneLease ? phoneHardwareTotal : 0)).toLocaleString()}/mo` : '—'}
                  </span>
                </div>
                {contractTerm && (
                  <div style={{ textAlign: 'right', fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                    {(contractTerm === 'annual' || contractTerm === '24-month') ? (contractTerm === '24-month' ? '24-month contract' : '12-month contract') : 'Month-to-month'}
                  </div>
                )}
                {/* ARR — annualized recurring revenue at full rollout */}
                {selectedPrices.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8, padding: '8px 10px', background: '#f0fdf4', border: '1px solid #a7f3d0', borderRadius: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: '0.05em' }}>ARR</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#047857' }}>
                      ${(Math.max(0, monthlyTotal + (isPhoneLease ? phoneHardwareTotal : 0)) * 12).toLocaleString()}
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Ramp schedule — visual rollout timeline (phased quotes only) */}
            {!isPhoneOnly && !isSdbMode && (() => {
              const activePhases = rampSchedule.filter(r => (parseInt(r.count) || 0) > 0);
              if (activePhases.length <= 1) return null;
              const perLocMonthly = effectiveSubtotal * (1 - discountPct / 100);
              let cumulative = 0;
              const rows = [...activePhases]
                .sort((a, b) => a.month - b.month)
                .map(r => {
                  const count = parseInt(r.count) || 0;
                  cumulative += count;
                  return { month: r.month, count, cumulative };
                });
              const assigned = cumulative;
              const unassigned = locationCount - assigned;
              return (
                <>
                  <div style={{ borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Ramp Schedule</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {rows.map(row => (
                      <div key={row.month}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                          <span style={{ fontSize: 12, color: '#374151' }}>
                            Month {row.month}
                            <span style={{ color: '#9ca3af', marginLeft: 5 }}>+{row.count} → {row.cumulative} live</span>
                          </span>
                          {selectedPrices.length > 0 && (
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>
                              ${Math.round(perLocMonthly * row.cumulative).toLocaleString()}/mo
                            </span>
                          )}
                        </div>
                        <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{
                            width: `${Math.min(100, (row.cumulative / locationCount) * 100)}%`,
                            height: '100%', background: '#1E6434', borderRadius: 3,
                            transition: 'width 200ms ease-out',
                          }} />
                        </div>
                      </div>
                    ))}
                    {unassigned !== 0 && (
                      <div style={{ fontSize: 11, color: '#dc2626' }}>
                        {unassigned > 0
                          ? `${unassigned} location${unassigned !== 1 ? 's' : ''} not yet assigned to a month`
                          : `${-unassigned} more assigned than the location count`}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}

            {/* One-time charges section */}
            {(showOnboardingInSummary || isPhonePurchase) && (
              <>
                <div style={{ borderTop: '2px solid #e5e7eb', margin: '16px 0 12px' }} />
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>One-Time Charges</div>
                  {showOnboardingInSummary && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, color: '#374151' }}>
                        {(onboardingFee && onboardingFee.product_name) || 'Platform Onboarding'}
                        {obPctOff > 0 && <span style={{ fontSize: 11, color: '#16a34a', marginLeft: 4 }}>({obPctOff}% off)</span>}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
                        ${effectiveOnboardingFee.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {isPhonePurchase && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, color: '#374151' }}>
                      {selectedPhoneHardware.product_name || 'Phone'} ({totalPhones} × ${(selectedPhoneHardware.unit_amount / 100).toLocaleString()} ea)
                      {hardwarePct > 0 && <span style={{ fontSize: 11, color: '#16a34a', marginLeft: 4 }}>({hardwarePct}% off)</span>}
                    </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
                        {hardwarePct > 0 && (
                          <span style={{ color: '#9ca3af', textDecoration: 'line-through', fontWeight: 400, marginRight: 5 }}>
                            ${((selectedPhoneHardware.unit_amount / 100) * totalPhones).toLocaleString()}
                          </span>
                        )}
                        ${phoneHardwareTotal.toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>One-Time Total</span>
                    <span style={{ fontSize: 18, fontWeight: 700, color: '#1E6434' }}>
                      ${((showOnboardingInSummary ? effectiveOnboardingFee : 0) + (isPhonePurchase ? phoneHardwareTotal : 0)).toLocaleString()}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      </div>

      {showContractViewer && contractPdfUrl && (
        <ContractViewer
          url={contractPdfUrl}
          title={`${orgName || 'Quote'}${currentQuoteId ? ` — ${currentQuoteId}` : ''}`}
          onClose={() => setShowContractViewer(false)}
        />
      )}
      </>
      )}

      {activeSubTab === 'quotes' && (
        <QuotesList
          embedded
          onEdit={(quote) => {
            loadQuoteForEditing(quote.quoteId);
            setActiveSubTab('builder');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}
    </div>
  );
};

// --- Insurance cost calculator ---
// Shown when a Newton Insurance price is on the quote. The rep enters the
// practice's appointments per month; we show what those verifications cost us
// at Zuub and let the rep type a custom per-location price. The custom price
// is stored as a per-product discount (list − custom) so billing, the contract,
// and the customer quote page all pick it up with no extra plumbing.
const InsuranceCostCalculator = ({ price, locationCount, apptsPerMonth, onApptsChange, discount, onDiscountChange }) => {
  const listPrice = price.unit_amount / 100;
  const interval = price.recurring?.interval || 'month';
  const netPrice = Math.max(0, listPrice - discount);
  // Local text so the rep can type freely; committed to the discount as they go.
  const [priceDraft, setPriceDraft] = useState(discount > 0 ? String(netPrice) : '');
  useEffect(() => {
    // Resync only when the draft no longer maps to the committed net price —
    // e.g. a quote loaded for editing. Blank and at/above-list drafts both
    // bill at list, so they map to listPrice and are left as typed.
    const typed = priceDraft === '' ? listPrice : parseFloat(priceDraft);
    const mapped = Number.isFinite(typed) && typed >= listPrice ? listPrice : typed;
    if (mapped !== netPrice) setPriceDraft(discount > 0 ? String(netPrice) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [netPrice, discount]);

  const appts = parseInt(apptsPerMonth) || 0;
  const costPerLoc = appts * ZUUB_COST_PER_VERIFICATION;
  const locs = Math.max(1, locationCount || 1);
  const totalCost = costPerLoc * locs;
  const totalRevenue = netPrice * locs;
  const margin = totalRevenue > 0 ? Math.round(((totalRevenue - totalCost) / totalRevenue) * 100) : null;
  const typedPrice = parseFloat(priceDraft);
  const aboveList = priceDraft !== '' && Number.isFinite(typedPrice) && typedPrice > listPrice;

  const commitPrice = (raw) => {
    setPriceDraft(raw);
    const val = parseFloat(raw);
    if (raw === '' || !Number.isFinite(val) || val < 0 || val >= listPrice) {
      onDiscountChange(0); // blank or at/above list → billed at list
      return;
    }
    onDiscountChange(Math.round((listPrice - val) * 100) / 100);
  };

  const money = (n) => `$${Math.round(n).toLocaleString()}`;
  const per = interval === 'month' ? 'mo' : interval;
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 };
  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ marginTop: 4, marginBottom: 14, padding: 16, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{price.product_name || 'Newton Insurance'} — custom pricing</span>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>Zuub cost is internal · not shown on the quote</span>
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ width: 200 }}>
          <label style={labelStyle}>Appointments / mo (per location)</label>
          <input
            type="number"
            min="0"
            step="1"
            value={apptsPerMonth}
            onChange={e => onApptsChange(e.target.value)}
            placeholder="e.g. 450"
            style={inputStyle}
          />
        </div>
        <div style={{ color: '#9ca3af', paddingBottom: 10, fontSize: 16 }}>→</div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={labelStyle}>Zuub cost</label>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, paddingBottom: 4 }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a' }}>{appts > 0 ? money(costPerLoc) : '—'}</span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              / location / {per}{appts > 0 ? ` · ${appts.toLocaleString()} × $${ZUUB_COST_PER_VERIFICATION.toFixed(2)}` : ''}
            </span>
          </div>
        </div>
        <div style={{ width: 200 }}>
          <label style={labelStyle}>Price / location / {per}</label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: 9, fontSize: 14, color: '#9ca3af' }}>$</span>
            <input
              type="number"
              min="0"
              step="1"
              value={priceDraft}
              onChange={e => commitPrice(e.target.value)}
              placeholder={String(listPrice)}
              style={{ ...inputStyle, paddingLeft: 24, borderColor: aboveList ? '#f59e0b' : '#d1d5db' }}
            />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb', fontSize: 12, color: '#6b7280' }}>
        <span>
          {locs} location{locs === 1 ? '' : 's'} · {appts > 0 ? `${money(totalCost)} / mo Zuub cost · ` : ''}{money(totalRevenue)} / mo revenue
          {discount > 0 && <span style={{ color: '#9ca3af' }}> · list {money(listPrice)}</span>}
        </span>
        <span>
          {aboveList ? (
            <span style={{ color: '#b45309', fontWeight: 600 }}>Above list — billed at {money(listPrice)}</span>
          ) : appts > 0 && margin !== null ? (
            <>Gross margin <span style={{ fontWeight: 600, color: margin >= 50 ? '#1E6434' : margin >= 30 ? '#b45309' : '#dc2626' }}>{margin}%</span></>
          ) : (
            <span>Enter appointments to see cost and margin</span>
          )}
        </span>
      </div>
    </div>
  );
};

// --- Apply to All Locations ---

const ApplyToAll = ({ locations, setLocations }) => {
  const [pms, setPms] = useState('');

  const apply = () => {
    setLocations(prev => prev.map(loc => ({
      ...loc,
      ...(pms ? { pms } : {}),
    })));
  };

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Set all</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>PMS</label>
        <select value={pms} onChange={e => setPms(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, color: '#374151' }}>
          <option value="">—</option>
          <option value="Curve">Curve</option>
          <option value="OpenDental">OpenDental</option>
          <option value="Dentrix">Dentrix</option>
          <option value="Eaglesoft">Eaglesoft</option>
          <option value="Ascend">Ascend</option>
          <option value="Denticon">Denticon</option>
        </select>
      </div>
      <button
        type="button"
        onClick={apply}
        disabled={!pms}
        style={{
          padding: '5px 14px', fontSize: 12, fontWeight: 600, borderRadius: 5, border: 'none', cursor: !pms ? 'not-allowed' : 'pointer',
          background: !pms ? '#e5e7eb' : '#1E6434', color: !pms ? '#9ca3af' : '#fff',
        }}
      >
        Apply to all
      </button>
    </div>
  );
};

// --- Reusable layout helpers ---

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <h2 style={{ fontSize: 16, fontWeight: 600, color: '#374151', marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #e5e7eb' }}>{title}</h2>
    {children}
  </div>
);

const FormRow = ({ children }) => (
  <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
    {React.Children.map(children, child => (
      <div style={{ flex: 1 }}>{child}</div>
    ))}
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 }}>
      {label}{required && <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>}
    </label>
    {React.cloneElement(children, {
      style: {
        width: '100%',
        padding: '8px 12px',
        border: '1px solid #d1d5db',
        borderRadius: 6,
        fontSize: 14,
        outline: 'none',
        boxSizing: 'border-box',
        ...(children.props.style || {}),
      }
    })}
  </div>
);

const tableInputStyle = { padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' };

export default QuoteBuilder;
